import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import type { TumProperty } from '@/types/tum';

export const useTumProperties = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const queryClient = useQueryClient();

  const { data: properties, isLoading, error } = useQuery({
    queryKey: ['tum-properties', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tum_properties')
        .select(`
          *,
          customer:tum_customers(*)
        `)
        .eq('workspace_id', currentWorkspace!.id)
        .order('property_name', { ascending: true });
      
      if (error) throw error;
      return data as TumProperty[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (property: Omit<TumProperty, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'workspace_id' | 'customer'>) => {
      const { data, error } = await supabase
        .from('tum_properties')
        .insert({ ...property, workspace_id: currentWorkspace!.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-properties'] });
      toast.success('Property created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create property: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TumProperty> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_properties')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-properties'] });
      toast.success('Property updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update property: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_properties')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-properties'] });
      toast.success('Property deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete property: ${error.message}`);
    },
  });

  return {
    properties: properties || [],
    isLoading,
    error,
    createProperty: createMutation.mutate,
    updateProperty: updateMutation.mutate,
    deleteProperty: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
